import { MessageCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { getDynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import { type OutreachHistoryWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { Button } from '@apps/web/components/ui/button';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { type StatusIntent } from '@apps/web/components/ui/status_tokens';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';

const COLLAPSED_ROW_LIMIT = 12;

function humanize(value: string): string {
    return value
        .split('_')
        .filter((part) => part.length > 0)
        .map((part) => `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`)
        .join(' ');
}

function statusIntent(value: string): StatusIntent {
    if (['filled', 'completed', 'available_interested'].includes(value)) {
        return 'positive';
    }
    if (['unfilled', 'failed', 'unavailable', 'cancelled'].includes(value)) {
        return 'negative';
    }
    if (['pending', 'pending_no_reply', 'no_response'].includes(value)) {
        return 'warning';
    }
    if (['in_progress', 'queued'].includes(value)) {
        return 'info';
    }
    return 'neutral';
}

function formatTimestamp(value: string, timezone: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(parsed);
}

export function OutreachHistoryWidget({
    model,
    timezone,
    loadError = false,
    hasMoreRows = false,
    isLoadingMoreRows = false,
    onShowAllRows,
    onCaregiverClick,
}: {
    readonly model: OutreachHistoryWidgetModel;
    readonly timezone: string;
    readonly loadError?: boolean;
    readonly hasMoreRows?: boolean;
    readonly isLoadingMoreRows?: boolean;
    readonly onShowAllRows?: () => void;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const [showAll, setShowAll] = useState(false);
    const metricTags = useMemo(
        () => getDynamicWidgetMetricTags(model),
        [model],
    );
    const visibleRows = showAll
        ? model.rows
        : model.rows.slice(0, COLLAPSED_ROW_LIMIT);
    const isPreview = model.rowProvenance === 'event_preview';

    if (model.rowCount === 0) {
        return (
            <div className="border-border/60 bg-background rounded-lg border p-3.5 shadow-sm">
                <p className="text-sm font-medium">No outreach history found</p>
            </div>
        );
    }

    return (
        <div className="border-border/60 bg-background max-w-full min-w-0 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2 border-b px-3.5 py-3">
                <div className="min-w-0">
                    <p className="text-[13px] font-semibold">
                        Outreach history
                    </p>
                    <p
                        className="text-muted-foreground text-xs"
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            'rowCount',
                        )}
                    >
                        {model.rowCount}{' '}
                        {model.rowCount === 1 ? 'history row' : 'history rows'}
                    </p>
                </div>
            </div>
            {model.issues.map((issue) => (
                <div key={issue.id} className="px-3.5 pt-2.5">
                    <DynamicWidgetIssueNotice
                        issue={issue}
                        metricTags={metricTags}
                    />
                </div>
            ))}
            {visibleRows.map((row, rowIndex) => {
                const title =
                    row.caregiverName ??
                    (row.outreachType === null
                        ? 'Outreach record unavailable'
                        : `${humanize(row.outreachType)} outreach`);
                const meta = [
                    row.outreachType === null
                        ? null
                        : humanize(row.outreachType),
                    row.channel?.toUpperCase() ?? null,
                    row.shiftStartTime === null
                        ? null
                        : `Shift ${formatTimestamp(row.shiftStartTime, timezone)}`,
                ].filter((value): value is string => value !== null);
                const displayStatus =
                    row.attemptStatus ?? row.outcome ?? row.status;
                const caregiverId = row.caregiverId;
                return (
                    <div
                        key={
                            row.attemptId ??
                            `${row.outreachId ?? 'history'}-${rowIndex}`
                        }
                        className="border-border/40 flex flex-wrap items-center gap-2.5 border-t px-3.5 py-2.5 text-[13px]"
                    >
                        {row.caregiverName !== null ? (
                            <UserAvatar
                                displayName={row.caregiverName}
                                colorKey={row.caregiverId ?? row.caregiverName}
                                className="size-7"
                            />
                        ) : (
                            <span className="bg-muted text-muted-foreground flex size-7 items-center justify-center rounded-full">
                                <MessageCircle
                                    className="size-3.5"
                                    aria-hidden="true"
                                />
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            {caregiverId !== null &&
                            onCaregiverClick !== undefined ? (
                                <button
                                    type="button"
                                    className="hover:text-primary font-medium underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                                    onClick={() =>
                                        onCaregiverClick(caregiverId)
                                    }
                                >
                                    {title}
                                </button>
                            ) : (
                                <p className="font-medium">{title}</p>
                            )}
                            {meta.length > 0 ? (
                                <p className="text-muted-foreground truncate text-xs">
                                    {meta.join(' · ')}
                                </p>
                            ) : null}
                            {row.outreachCreatedAt !== null ? (
                                <p className="text-muted-foreground text-xs">
                                    Created{' '}
                                    {formatTimestamp(
                                        row.outreachCreatedAt,
                                        timezone,
                                    )}
                                    {row.responseAt === null
                                        ? ''
                                        : ` · Responded ${formatTimestamp(
                                              row.responseAt,
                                              timezone,
                                          )}`}
                                </p>
                            ) : null}
                        </div>
                        {displayStatus !== null ? (
                            <StatusBadge
                                status={{
                                    label: humanize(displayStatus),
                                    intent: statusIntent(displayStatus),
                                }}
                            />
                        ) : null}
                    </div>
                );
            })}
            <div className="border-border/60 bg-muted/40 flex flex-wrap items-center gap-2 rounded-b-lg border-t px-3.5 py-2.5">
                {model.rows.length > visibleRows.length || hasMoreRows ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isLoadingMoreRows}
                        onClick={() => {
                            setShowAll(true);
                            onShowAllRows?.();
                        }}
                    >
                        {isLoadingMoreRows
                            ? 'Loading history'
                            : `Show all ${model.rowCount} rows`}
                    </Button>
                ) : null}
                {isPreview ? (
                    <p className="text-muted-foreground ml-auto text-xs">
                        {loadError
                            ? 'Preview only — full outreach history is unavailable.'
                            : `Preview · ${model.rows.length} of ${model.rowCount} rows`}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
